import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { Link } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import StockMovementChart from "src/components/charts/StockMovementChart";
import SalesOverviewChart from "src/components/charts/SalesOverviewChart";
import PurchaseOverviewChart from "src/components/charts/PurchaseOverviewChart";
import CategorySalesChart from "src/components/charts/CategorySalesChart";
import TopProductsChart from "src/components/charts/TopProductsChart";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import { currencyFormatter } from "src/utils/common";
import LowStockTable from "src/components/dashboard/LowStockTable";
import RecentSalesTable from "src/components/dashboard/RecentSalesTable";
import RecentPurchaseTable from "src/components/dashboard/RecentPurchaseTable";
import Popover from "src/components/ui/Popover";
import { Info } from "lucide-react";

const DashboardPage: React.FC = () => {
  // const {
  //   user: { data: user },
  // } = useAppSelector((state) => state.authTenant);
  // if (user?.userType === "IN_HOUSE" && user?.role === "admin")
  //   return <SuperAdminDashboard />;
  // if (user?.role === "admin") return <TenantAdminDashboard />;
  // else if (user?.role === "manager") return <ManagerDashboard />;
  // else if (user?.role === "staff") return <StaffDashboard />;
  // return null;

  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);

  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const permissionEntries = Object.keys(permissions || {});
  const shouldFilter = permissionEntries.length > 0 && !isTenantAdmin;
  const canSeeModule = (module: string) => {
    if (!shouldFilter) return true;
    return (permissions?.[module] || []).length > 0;
  };

  const dispatch = useAppDispatch();

  const {
    tenantDashboardSummary,
    tenantStockMovement,
    tenantSalesOverview,
    tenantPurchaseOverview,
    tenantCategorySales,
    tenantTopProducts,
    tenantRecentSales,
    tenantRecentPurchases,
    tenantLowStockAlerts,
  } = useAppSelector((state) => state.dashboard);

  const showProducts = canSeeModule("products");
  const showInventory = canSeeModule("inventory");
  const showSales = canSeeModule("sales");
  const showPurchases = canSeeModule("purchases");
  const showStockMovement = showInventory || showProducts;
  const showLowStock = showInventory || showProducts;
  const needsSummary =
    showProducts || showInventory || showSales || showPurchases;
  const canReorder =
    isTenantAdmin || (permissions?.purchases || []).includes("create");

  useEffect(() => {
    if (needsSummary) {
      dispatch(dashboardActions.getTenantDashboardSummaryRequest({}));
    }
    if (showStockMovement) {
      dispatch(
        dashboardActions.getTenantStockMovementRequest({ days: 7 })
      );
    }
    if (showSales) {
      dispatch(
        dashboardActions.getTenantSalesOverviewRequest({ days: 7 })
      );
    }
    if (showPurchases) {
      dispatch(
        dashboardActions.getTenantPurchaseOverviewRequest({ days: 7 })
      );
    }
    if (showSales) {
      dispatch(
        dashboardActions.getTenantCategorySalesRequest({
          days: 7,
          limit: 6,
        })
      );
      dispatch(
        dashboardActions.getTenantTopProductsRequest({
          days: 7,
          limit: 6,
        })
      );
      dispatch(
        dashboardActions.getTenantRecentSalesRequest({ limit: 5 })
      );
    }
    if (showPurchases) {
      dispatch(
        dashboardActions.getTenantRecentPurchasesRequest({ limit: 5 })
      );
    }
    if (showLowStock) {
      dispatch(
        dashboardActions.getTenantLowStockAlertsRequest({ limit: 5 })
      );
    }
  }, [
    dispatch,
    needsSummary,
    showStockMovement,
    showSales,
    showPurchases,
    showLowStock,
  ]);

  const summary = tenantDashboardSummary.data;
  const stockMovementData = tenantStockMovement.data?.data || [];
  const salesOverviewData = tenantSalesOverview.data?.data || [];
  const purchaseOverviewData = tenantPurchaseOverview.data?.data || [];
  const categorySalesData = tenantCategorySales.data?.data || [];
  const topProductsData = tenantTopProducts.data?.data || [];
  const recentSalesRows = tenantRecentSales.data?.data || [];
  const recentPurchaseRows = tenantRecentPurchases.data?.data || [];
  const lowStockRows = showLowStock
    ? tenantLowStockAlerts.data?.products || []
    : [];

  const kpiCards = [
    showProducts && {
      label: "Total Products",
      value: summary?.totalProducts ?? 0,
      description: "Count of active products in your catalog.",
      actionLabel: "View products",
      actionPath: "/app/product/list",
    },
    showInventory && {
      label: "Stock Value",
      value: currencyFormatter(summary?.stockValue ?? 0),
      description: "Total inventory value based on current stock and pricing.",
      actionLabel: "View inventory",
      actionPath: "/app/inventory",
    },
    showInventory && {
      label: "Low Stock Alerts",
      value: summary?.lowStockCount ?? 0,
      description:
        "Products currently below their configured minimum stock threshold.",
      actionLabel: "View low stock",
      actionPath: "/app/product/list",
    },
    showSales && {
      label: "Sales Orders",
      value: summary?.totalSalesOrders ?? 0,
      description: "Total number of sales orders created.",
      actionLabel: "View sales",
      actionPath: "/app/sale/list",
    },
    showSales && {
      label: "Pending Sales",
      value: summary?.pendingSalesOrders ?? 0,
      description: "Sales orders that are not yet completed or closed.",
      actionLabel: "View pending sales",
      actionPath: "/app/sale/list",
    },
    showPurchases && {
      label: "Purchase Orders",
      value: summary?.totalPurchaseOrders ?? 0,
      description: "Total number of purchase orders created.",
      actionLabel: "View purchases",
      actionPath: "/app/purchase/list",
    },
    showPurchases && {
      label: "Pending Purchases",
      value: summary?.pendingPurchaseOrders ?? 0,
      description: "Purchase orders that are not yet completed or closed.",
      actionLabel: "View pending purchases",
      actionPath: "/app/purchase/list",
    },
  ].filter(Boolean) as {
    label: string;
    value: string | number;
    description?: string;
    actionLabel?: string;
    actionPath?: string;
  }[];

  return (
    <Layout
    // meta={{
    //   title: "Dashboard | Invyntra",
    //   description: "",
    //   keywords: "sales, products, invoices, inventory, invyntra",
    // }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title={`Welcome ${user?.firstName}!`}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Dashboard", path: "/dashboard" },
          ]}
        />

        {/* Dashboard Content */}
        <div className="space-y-6">
          {kpiCards.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {kpiCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-gray-500">{card.label}</p>
                    {card.description && (
                      <Popover
                        openOnHover
                        trigger={
                          <button className="text-gray-400 hover:text-gray-600">
                            <Info size={14} />
                          </button>
                        }
                      >
                        <div className="max-w-xs px-3 py-2 text-xs text-gray-600">
                          {card.description}
                        </div>
                      </Popover>
                    )}
                  </div>
                  <p className="mt-2 text-2xl font-semibold text-gray-900">
                    {card.value}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="h-1 w-12 rounded-full bg-primary-200" />
                    {card.actionPath && card.actionLabel && (
                      <Link
                        to={card.actionPath}
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        {card.actionLabel}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(showStockMovement || showSales || showPurchases) && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {showStockMovement && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Stock in vs. Stock Out
                    </h2>
                    <span className="text-xs text-gray-400">Last 7 days</span>
                  </div>
                  <StockMovementChart data={stockMovementData} />
                </div>
              )}

              {showSales && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Sales Overview
                    </h2>
                    <span className="text-xs text-gray-400">This week</span>
                  </div>
                  <SalesOverviewChart data={salesOverviewData} />
                </div>
              )}

              {showPurchases && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Purchase Overview
                    </h2>
                    <span className="text-xs text-gray-400">This week</span>
                  </div>
                  <PurchaseOverviewChart data={purchaseOverviewData} />
                </div>
              )}
            </div>
          )}

          {showLowStock && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700">
                Low Stock Alerts
              </h2>
              <LowStockTable rows={lowStockRows} canReorder={canReorder} />
            </div>
          )}

          {(showSales || showProducts) && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {showSales && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Category-wise Sales
                    </h2>
                    <span className="text-xs text-gray-400">Last 7 days</span>
                  </div>
                  <CategorySalesChart data={categorySalesData} />
                </div>
              )}

              {showSales && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Top Selling Products
                    </h2>
                    <span className="text-xs text-gray-400">Last 7 days</span>
                  </div>
                  <TopProductsChart data={topProductsData} />
                </div>
              )}
            </div>
          )}

          {(showSales || showPurchases) && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {showSales && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Recent Sales Orders
                    </h2>
                    <span className="text-xs text-gray-400">Latest 5</span>
                  </div>
                  <RecentSalesTable rows={recentSalesRows} />
                </div>
              )}

              {showPurchases && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Recent Purchase Orders
                    </h2>
                    <span className="text-xs text-gray-400">Latest 5</span>
                  </div>
                  <RecentPurchaseTable rows={recentPurchaseRows} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
